import React, { useState, useMemo } from 'react';
import * as Bixby from '$/core';
import { toastError, toastSuccess } from '$/store/toast/actions';
import { downloadFile } from '$/store/export/actions';
import { connect } from 'react-redux';
import { AppState } from '$/store';
import './index.scss';
import BixButton from '$/components/global/bixButton';
import { DocumentsAdminService } from '$/services';
import { FileValidationRequest } from '$/services/DocumentsAdminService';
import { Navigate } from 'react-router-dom';
import ClientValidation from './clientValidation';
import CompanyDocumentFileUpload from './companyDocumentFileUpload';
import { v4 as uuidv4 } from 'uuid';
import groupBy from 'lodash/groupBy';
import BixCheckbox from '$/components/global/bixCheckbox';

interface IDocumentsProps {
  companyDocumentCategories: Bixby.Models.CompanyDocumentCategory[];
  currentCompany?: Bixby.Models.AdminCompany;
  //hasTags?: boolean | undefined;
  dispatchToastSuccess: (message: string) => Promise<void>;
  dispatchToastError: (message: string) => Promise<void>;
  dispatchDownloadDocument: (blob: Blob, fileName: string) => Promise<void>;
}

const CompanyDocumentsUpload: React.FC<IDocumentsProps> = ({
  currentCompany,
  companyDocumentCategories,
  //hasTags,
  dispatchToastSuccess,
  dispatchToastError,
  dispatchDownloadDocument,
}) => {
  const [droppedFiles, setDroppedFiles] = useState<Bixby.Models.IDocumentFile[]>([]); // for a zip file, all droppedFiles will have the zip file as their file attribute
  const [isValidating, setIsValidating] = useState(false);
  const [hasBeenValidated, setHasBeenValidated] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadSuccessful, setUploadSuccessful] = useState(false);
  const [prevalidationError, setPrevalidationError] = useState(undefined as string | undefined);
  const [validationError, setValidationError] = useState(undefined as string | undefined);
  const [uploadError, setUploadError] = useState(undefined as string | undefined);
  const [clientDataSiteProvider, setClientDataSiteProvider] = useState(
    undefined as Bixby.Models.ClientDataSiteProvider | undefined
  );
  const [isPreValidating, setIsPrevalidating] = useState(false);
  const [watermarkConferValue, setWatermarkConferValue] = useState(false);
  const [sendEmailValue, setSendEmailValue] = useState(false);

  const requiresWatermarkConfer = useMemo(() => {
    if (!currentCompany) return false;

    return (
      !currentCompany.isWatermarkLocked &&
      !clientDataSiteProvider &&
      currentCompany.dataSiteProvider_Name.toLowerCase() !== Bixby.Constants.DATASITEPROVDIER_SYNDTRAK.toLowerCase()
    );
  }, [currentCompany, clientDataSiteProvider]);

  const hasEmailAlertPermissions = useMemo(() => {
    if (!currentCompany || !currentCompany.clients) return false;

    const found = currentCompany.clients.filter(_ => _.hasDocUploadAlert);
    if (clientDataSiteProvider) {
      setSendEmailValue(false);
      const wmClientFound = found.filter(_ => _.client_Name == clientDataSiteProvider.client_Name);
      //if wm upload only show if that client has alerts
      return wmClientFound !== undefined && wmClientFound.length > 0;
    } else {
      setSendEmailValue(false);
      //if company has an associated client that has news alerts enabled
      return found !== undefined && found.length > 0;
    }
  }, [clientDataSiteProvider, currentCompany]);

  const isValidateDisabled = useMemo(() => {
    if (droppedFiles.length === 0 || isValidating) {
      return true;
    }

    const invalidFiles = droppedFiles.filter(file => file.hasDuplicateNameValidationError !== false || !file.title);

    return invalidFiles.length > 0;
  }, [droppedFiles, isValidating]);

  const isSubmitDisabled = useMemo(() => {
    if (droppedFiles.length === 0) {
      return true;
    }

    // valid files are files that do not have any server validation errors
    // already existing non-watermarked documents do not count as valid documents for the purposes of enabling the submit button
    const validFiles = droppedFiles.filter(
      file => file.hasDuplicateNameValidationError == false && file.hasServerValidationError == false
    );

    // invalid files have a server validation error that is not equal to the 'documents already exist' errors
    const invalidFiles = droppedFiles.filter(file => {
      const hasAlreadyExistsError =
        file.error == Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
        file.error == Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS;

      const missingCategoriesError =
        !file.companyDocumentCompanyDocumentCategories || file.companyDocumentCompanyDocumentCategories.length === 0;

      return (
        file.hasDuplicateNameValidationError !== false ||
        (file.hasServerValidationError !== false && !hasAlreadyExistsError) ||
        (missingCategoriesError && clientDataSiteProvider)
        // to be uncommented post inital doc upload phase
        // || (missingCategoriesError && hasTags != undefined && hasTags)
      );
    });

    if (requiresWatermarkConfer) {
      return validFiles.length <= 0 || invalidFiles.length > 0 || !watermarkConferValue;
    } else {
      return validFiles.length <= 0 || invalidFiles.length > 0;
    }
  }, [clientDataSiteProvider, droppedFiles, /*hasTags,*/ requiresWatermarkConfer, watermarkConferValue]);

  const checkForDuplicates = (files: Bixby.Models.IDocumentFile[]) => {
    // check for duplicates
    const filesGroupedByName = groupBy(files, file => file.fileName);
    const dupes = Object.keys(filesGroupedByName).filter(_ => filesGroupedByName[_].length > 1);
    const previousDupes = Object.keys(filesGroupedByName).filter(
      _ => filesGroupedByName[_].length === 1 && filesGroupedByName[_][0].hasDuplicateNameValidationError === true
    );

    // mark previous dupes as valid
    previousDupes.forEach(nonDupeFileName => {
      const nonDupeFiles = filesGroupedByName[nonDupeFileName];
      nonDupeFiles[0].hasDuplicateNameValidationError = false;
    });

    dupes.forEach(dupeFileName => {
      const dupeFiles = filesGroupedByName[dupeFileName];

      // set to true by default
      dupeFiles.forEach(file => {
        file.hasDuplicateNameValidationError = true;
      });

      // if all files have a row number, and all files have a different row number, then set duplicate name validation error to false
      const allFilesHaveRowNumbers = dupeFiles.every(_ => !!_.rowNumber);
      if (allFilesHaveRowNumbers) {
        const allFilesHaveDifferentRowNumbers =
          Object.keys(groupBy(dupeFiles, _ => _.rowNumber)).length === dupeFiles.length;
        if (allFilesHaveDifferentRowNumbers) {
          dupeFiles.forEach(file => {
            file.hasDuplicateNameValidationError = false;
          });
        }
      }
    });
  };

  const handleDroppedFilesAndCheckDupes = async (files: Bixby.Models.IDocumentFile[]) => {
    // handle clear
    if (files.length <= 0) {
      setPrevalidationError(undefined);
      setValidationError(undefined);
      setDroppedFiles([]);
      setHasBeenValidated(false);
    }

    checkForDuplicates(files);
    setDroppedFiles(files);
  };

  const handleDownloadDocument = async (id: string, index?: number) => {
    const found = droppedFiles.find(_ => _.id === id);

    if (found && found.submissionType === Bixby.Enums.CompanyDocumentSubmissionType.Zip) {
      if (index && index > -1) {
        const { blob, filename } = await DocumentsAdminService.downloadFromZipFile(found.file, found.fileName, index);
        if (filename) {
          await dispatchDownloadDocument(blob, filename);
        }
        return;
      }

      const filesGroupedByName = groupBy(droppedFiles, file => file.fileName);
      const dupes = Object.keys(filesGroupedByName).filter(_ => filesGroupedByName[_].length > 1);
      index = undefined;
      if (dupes.length > 0 && dupes.includes(found.fileName)) {
        const foundIndex = filesGroupedByName[found.fileName].findIndex(_ => _.id === found.id);
        index = foundIndex > -1 ? foundIndex : undefined;
      }
      const { blob, filename } = await DocumentsAdminService.downloadFromZipFile(found.file, found.fileName, index);
      if (filename) {
        await dispatchDownloadDocument(blob, filename);
      }
    } else if (found && found.submissionType === Bixby.Enums.CompanyDocumentSubmissionType.Files) {
      await dispatchDownloadDocument(found.file, found.title);
    }
  };

  const handlePreValidateFiles = async (files: Bixby.Models.IDocumentFile[]) => {
    try {
      if (!currentCompany) return;
      setIsPrevalidating(true);
      const zipFiles = files.filter(_ => _.file.name.endsWith('.zip'));
      const areZipFilesUnique = zipFiles.every(_ => _.file.name === files[0].file.name);
      const nonZipFiles = files.filter(_ => !_.file.name.endsWith('.zip'));

      if (zipFiles.length > 1 && !areZipFilesUnique) {
        setPrevalidationError('Cannot submit more than one zip file at a time.');
        return;
      }

      const clientDataSiteProvider_Id = clientDataSiteProvider ? clientDataSiteProvider.id : undefined;

      if (nonZipFiles.length >= 1 && zipFiles.length == 0) {
        const fileNames = files.map(_ => _.file.name);
        const results = await DocumentsAdminService.prevalidateFiles(
          currentCompany.id,
          fileNames,
          clientDataSiteProvider_Id
        );
        const mapped = results.map(result => {
          const foundIx = files.findIndex(_ => _.file.name === result.fileName);

          const found = files[foundIx];
          found.title = result.title || found.title;
          found.description = result.description || found.description;
          found.companyDocumentCompanyDocumentCategories = result.companyDocumentCompanyDocumentCategories;
          found.submissionType = Bixby.Enums.CompanyDocumentSubmissionType.Files;
          found.rowNumber = result.rowNumber || undefined;
          found.hasDuplicateNameValidationError = false;
          found.error = result.error;
          found.documentDeclarationType = result.documentDeclarationType;

          const hasAlreadyExistsError =
            result.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS;
          found.hasServerValidationError =
            !result.isValid ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND ||
            hasAlreadyExistsError;
          found.requireAlreadyExistsOverride = hasAlreadyExistsError;

          // remove from array in case we have duplicate files so it doesn't get picked up again
          files.splice(foundIx, 1);
          return found;
        });

        handleDroppedFilesAndCheckDupes(
          droppedFiles.concat([...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])])
        );
      }

      if (zipFiles.length >= 1 && areZipFilesUnique && nonZipFiles.length == 0) {
        const results = await DocumentsAdminService.prevalidateZip(
          currentCompany.id,
          zipFiles[0].file,
          clientDataSiteProvider_Id
        );

        const mapped = results.map(result => {
          let calculatedTitle = result.fileName.split('/').pop();
          if (calculatedTitle && calculatedTitle.length > 0) {
            calculatedTitle = calculatedTitle.replace(/\.[^/.]+$/, '');
          }

          const model: Bixby.Models.IDocumentFile = {
            id: uuidv4(),
            fileName: result.fileName,
            title: calculatedTitle || result.fileName.replace(/\.[^/.]+$/, ''),
            description: result.description,
            file: zipFiles[0].file,
            companyDocumentCompanyDocumentCategories: result.companyDocumentCompanyDocumentCategories,
            rowNumber: result.rowNumber,
            index: result.index,
            hasDuplicateNameValidationError: results.filter(_ => _.fileName === result.fileName).length > 1,
            error: result.error,
            submissionType: Bixby.Enums.CompanyDocumentSubmissionType.Zip,
            documentDeclarationType: result.documentDeclarationType,
          };
          const hasAlreadyExistsError =
            result.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS;
          model.hasServerValidationError =
            !result.isValid ||
            result.error === Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND ||
            hasAlreadyExistsError;
          model.requireAlreadyExistsOverride = hasAlreadyExistsError;

          return model;
        });

        handleDroppedFilesAndCheckDupes(
          droppedFiles.concat([...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])])
        );
      }
      dispatchToastSuccess('Prevalidation check complete');
    } catch (err: any) {
      dispatchToastError('Error prevalidating documents');
    } finally {
      setIsPrevalidating(false);
    }
  };

  const handleValidation = async () => {
    try {
      if (!currentCompany) return;

      setIsValidating(true);
      setValidationError(undefined);

      const documents = droppedFiles.map((file: Bixby.Models.IDocumentFile) => {
        const model: FileValidationRequest = {
          id: file.id,
          fileName:
            file.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Zip ? file.fileName : file.file.name,
          title: file.title,
          description: file.description || '',
          companyDocumentCategory_Ids: file.companyDocumentCompanyDocumentCategories
            ? file.companyDocumentCompanyDocumentCategories.map(_ => _.companyDocumentCategory_Id)
            : [],
          clientDataSiteProvider_Id: (clientDataSiteProvider || {}).id,
          rowNumber: file.rowNumber,
          index: file.index,
          overrideAlreadyExistsError: file.overrideAlreadyExistsError,
        };
        return model;
      });
      const results = await DocumentsAdminService.validateDocuments(currentCompany.id, documents);
      const mapped = results.map(result => {
        const found = droppedFiles.find(_ => _.id === result.inputFileId);
        if (!found) return;
        found.error = result.error;
        found.rowNumber = result.rowNumber;
        found.description = result.description;
        found.isWatermarked = result.isWatermarked;
        // duplicate, non-watermarked files will have a server validation error but are considered valid
        // files with multiple excel matches are valid but will also have an error
        const hasAlreadyExistsError =
          result.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
          result.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS;
        found.hasServerValidationError =
          !result.isValid ||
          result.error === Bixby.Constants.DOCUMENT_HOSTING_MULTIPLE_EXCEL_MATCHES_FOUND ||
          hasAlreadyExistsError;
        found.requireAlreadyExistsOverride = found.requireAlreadyExistsOverride || hasAlreadyExistsError;
        return found;
      });
      setDroppedFiles([...(mapped.filter(_ => !!_) as Bixby.Models.IDocumentFile[])]);
      dispatchToastSuccess('Validation check complete.');
      setHasBeenValidated(true);
    } catch (err: any) {
      dispatchToastError('Validation check failed');
      if (err && err.response && err.response.data && err.response.data.error) {
        setValidationError(err.response.data.error);
      } else {
        setValidationError('Error validating documents');
      }
    } finally {
      setIsValidating(false);
    }
  };

  const handleSubmit = async () => {
    try {
      if (!currentCompany) return;

      setIsSubmitting(true);
      setUploadError(undefined);

      const documents = droppedFiles
        .filter((file: Bixby.Models.IDocumentFile) => {
          // remove files that already exist from the list of documents to submit unless they have been overridden
          // this simplifies the file uploading process for the user
          if (
            file.error === Bixby.Constants.DOCUMENT_HOSTING_WATERMARKED_DOCUMENT_ALREADY_EXISTS ||
            file.error === Bixby.Constants.DOCUMENT_HOSTING_NON_WATERMARKED_DOCUMENT_ALREADY_EXISTS
          ) {
            return file.overrideAlreadyExistsError;
          }
          return true;
        })
        .map((file: Bixby.Models.IDocumentFile) => {
          const model: Bixby.Models.CompanyDocumentSubmission = {
            id: file.id,
            title: file.title,
            fullFileName: file.fileName,
            description: file.description || '',
            companyDocumentCompanyDocumentCategories: file.companyDocumentCompanyDocumentCategories || [],
            clientDataSiteProvider_Id: (clientDataSiteProvider || {}).id,
            companyDocumentTags: file.tags,
            hasFile: false,
            datePosted: file.datePosted,
            rowNumber: file.rowNumber,
            index: file.index,
            overrideAlreadyExistsError: file.overrideAlreadyExistsError,
            sendEmailChecked: sendEmailValue,
          };
          return model;
        });
      const individualFiles = droppedFiles.filter(
        _ =>
          _.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Files && documents.find(doc => doc.id === _.id)
      );
      const zipFiles = droppedFiles.filter(
        _ => _.submissionType == Bixby.Enums.CompanyDocumentSubmissionType.Zip && documents.find(doc => doc.id === _.id)
      );

      if (individualFiles.length > 0) {
        // submitting files all at once
        const docs: Bixby.Models.CompanyDocumentSubmission[] = [];
        const files: File[] = [];
        for (const [, file] of individualFiles.entries()) {
          const doc = documents.find(_ => _.id === file.id);
          if (!doc) continue;
          files.push(new File([file.file], file.file.name, { type: file.file.type }));
          docs.push(doc);
        }

        await DocumentsAdminService.submitBulkDocuments(currentCompany.id, docs, files);
      }

      if (zipFiles.length > 0) {
        await DocumentsAdminService.submitZipFile(
          currentCompany.id,
          zipFiles[0].file,
          documents.filter(_ => zipFiles.map(_ => _.id).includes(_.id))
        );
      }

      dispatchToastSuccess('Successfully submitted documents.');
      setIsSubmitting(false);
      setHasBeenValidated(false);
      setUploadSuccessful(true);
    } catch (err: any) {
      dispatchToastError(`Document upload error`);
      if (err && err.response && err.response.data && err.response.data.error) {
        setUploadError(`${err.response.data.error}`);
      } else {
        setUploadError('');
      }
      setIsSubmitting(false);
      setHasBeenValidated(false);
    }
  };

  const handleClientDataSiteProviderValidation = (model: Bixby.Models.ClientDataSiteProvider | undefined) => {
    setClientDataSiteProvider(model);
  };

  if (uploadSuccessful) {
    return <Navigate to={`/admin/company/${currentCompany?.id}/document-hosting`} />;
  } else {
    return companyDocumentCategories ? (
      <section className="file-upload">
        {currentCompany?.isWatermarkLocked && (
          <p className="error watermark-error">
            This is a locked watermarked site. You are required to complete Step 1 before uploading documents.
          </p>
        )}
        <h4>Step 1 - Enter Client Identifier ({currentCompany?.dataSiteProvider_Name})</h4>
        <ClientValidation
          currentCompanyId={currentCompany?.id ?? ''}
          clientDataSiteProvider={clientDataSiteProvider}
          onValidityChange={handleClientDataSiteProviderValidation}
        />
        <h4>Step 2 - Upload Files{clientDataSiteProvider ? ` for ${clientDataSiteProvider.client_Name}` : ''}</h4>
        {!!prevalidationError && <p className="error">{prevalidationError}</p>}
        <CompanyDocumentFileUpload
          currentCompany={currentCompany}
          companyDocumentCategories={companyDocumentCategories}
          droppedFiles={droppedFiles}
          onSetDroppedFiles={handleDroppedFilesAndCheckDupes}
          onPreValidateFiles={handlePreValidateFiles}
          clientDataSiteProvider={clientDataSiteProvider}
          isProcessingFiles={isPreValidating}
          dispatchToastError={dispatchToastError}
          onDownloadDocument={handleDownloadDocument}
        />
        <h4>Step 3 - Validation</h4>
        <div className="server-validation-contain">
          <BixButton
            type="button"
            useSmall
            disabled={isValidateDisabled}
            useLoading
            isLoading={isValidating}
            onClick={handleValidation}>
            Validate
          </BixButton>
        </div>
        {!!validationError && <p className="error">{validationError}</p>}
        <h4>Step 4 - Submit</h4>
        {requiresWatermarkConfer && (
          <div>
            <BixCheckbox
              id="watermarkConferValue"
              checked={watermarkConferValue}
              onChange={event => {
                const target = event.target as HTMLInputElement;
                setWatermarkConferValue(target.checked);
              }}>
              I confer the documents don't have watermarks.
            </BixCheckbox>
          </div>
        )}
        {hasEmailAlertPermissions && (
          <div className="acceptance_wrap">
            <BixCheckbox
              id="sendEmailValue"
              checked={sendEmailValue}
              onChange={event => {
                const target = event.target as HTMLInputElement;
                setSendEmailValue(target.checked);
              }}>
              Send document upload alert to clients.
            </BixCheckbox>
          </div>
        )}
        <div className="server-validation-contain">
          <BixButton
            type="button"
            useSmall
            disabled={isSubmitDisabled || !hasBeenValidated}
            useLoading
            isLoading={isSubmitting}
            onClick={handleSubmit}>
            Submit
          </BixButton>
        </div>
        {!!isSubmitting && <p>Uploading documents...</p>}
        {!!uploadError && (
          <>
            <p className="error">{`Error submitting document`}</p>
            <p className="error">{uploadError}</p>
          </>
        )}
      </section>
    ) : (
      <></>
    );
  }
};

const mapStateToProps = (state: AppState) => {
  return {
    currentCompany: state.companyState.currentCompany,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchDownloadDocument: (blob: Blob, fileName: string) => dispatch(downloadFile(blob, fileName)),
  dispatchDownloadFailure: () => dispatch(toastError('Failed to download document')),
  dispatchToastSuccess: (message: string) => dispatch(toastSuccess(message)),
  dispatchToastError: (message: string) => dispatch(toastError(message)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CompanyDocumentsUpload);
